import Button from "../../../../shared/components/Button";
import GradientShareButton from "../../../../shared/components/GradientShareButton";
import ImageUploadField from "../../../../shared/components/ImageUploadField";
import InfoTooltip from "../../../../shared/components/InfoTooltip";
import UserAvatar from "../../../../shared/components/UserAvatar";
import { siteTexts } from "../../../../shared/i18n/siteTexts";
import type { AuthUser } from "../../../auth/shared/types/auth";
import type { ProfileEventStats } from "./tabs/ProfileEventsTab";

type UserProfileTab = "profile" | "security" | "challenges" | "registrations";
type ProfileSidebarAccordionKey =
  | "achievements"
  | "categoryRequest"
  | "statistics"
  | "history"
  | "myGames"
  | "openMatches"
  | "registrations"
  | "notifications"
  | "organizerRanking"
  | "visualIdentity"
  | "security";

type ProfileSidebarProps = {
  user: AuthUser;
  tab: UserProfileTab;
  showCategoryRequestForm: boolean;
  hasActiveProfileAccordion: boolean;
  openProfileAccordions: Record<ProfileSidebarAccordionKey, boolean>;
  avatarDraft: string;
  sexDraft: AuthUser["sex"];
  stats: ProfileEventStats;
  onSaveAvatarCrop: (nextAvatarUrl: string) => void;
  onRemoveAvatar: () => void;
  onSelectOverview: () => void;
  onNavigateAccordion: (key: ProfileSidebarAccordionKey) => void;
  onOpenShare: () => void;
  onOpenCategoryRequest: () => void;
};

function profileRoleLabel(role: AuthUser["role"]) {
  if (role === "admin") return siteTexts.profile.user.roleLabels.admin;
  if (role === "organizer") return siteTexts.profile.user.roleLabels.organizer;
  return siteTexts.profile.user.roleLabels.user;
}

export default function ProfileSidebar({
  user,
  tab,
  showCategoryRequestForm,
  hasActiveProfileAccordion,
  openProfileAccordions,
  avatarDraft,
  sexDraft,
  stats,
  onSaveAvatarCrop,
  onRemoveAvatar,
  onSelectOverview,
  onNavigateAccordion,
  onOpenShare,
  onOpenCategoryRequest,
}: ProfileSidebarProps) {
  const dashboardTexts = siteTexts.profile.user.dashboard;
  const info = siteTexts.common.info;

  const navItems: Array<{
    key: ProfileSidebarAccordionKey;
    icon: string;
    label: string;
    organizerOnly?: boolean;
  }> = [
    {
      key: "security",
      icon: "lock",
      label: siteTexts.profile.user.tabs.security,
    },
    {
      key: "history",
      icon: "emoji_events",
      label: dashboardTexts.eventsTitle,
    },
    {
      key: "registrations",
      icon: "receipt_long",
      label: siteTexts.profile.user.tabs.registrations,
    },
    {
      key: "myGames",
      icon: "sports_tennis",
      label: siteTexts.profile.myGames.title,
    },
    {
      key: "openMatches",
      icon: "group_add",
      label: siteTexts.profile.openMatches.title,
    },
    {
      key: "achievements",
      icon: "workspace_premium",
      label: siteTexts.profile.user.achievements.title,
    },
    {
      key: "notifications",
      icon: "notifications",
      label: siteTexts.profile.user.notificationPreferences.title,
    },
    {
      key: "statistics",
      icon: "monitoring",
      label: siteTexts.profile.user.organizerStatistics.title,
      organizerOnly: true,
    },
    {
      key: "organizerRanking",
      icon: "leaderboard",
      label: siteTexts.profile.user.organizerRanking.title,
      organizerOnly: true,
    },
    {
      key: "visualIdentity",
      icon: "palette",
      label: siteTexts.profile.user.visualIdentity.title,
      organizerOnly: true,
    },
  ];

  return (
    <aside className="user-profile-sidebar">
      <div className="user-profile-card user-profile-summary-card">
        <div className="user-profile-summary">
          <div
            className="user-profile-avatar"
            aria-label={siteTexts.profile.user.avatarAriaLabel}
          >
            <UserAvatar
              avatarUrl={avatarDraft || user.avatarUrl}
              name={user.name}
              sex={sexDraft || user.sex}
              size={125}
            />
            <ImageUploadField
              aspectRatio={1}
              label={siteTexts.profile.user.labels.avatar}
              maxFileBytes={500 * 1024}
              outputWidth={512}
              applyCropLabel={siteTexts.profile.user.saveAvatarCrop}
              previewCircle
              value={avatarDraft}
              onChange={onSaveAvatarCrop}
              onRemove={onRemoveAvatar}
              renderTrigger={({ openFilePicker }) => (
                <button
                  type="button"
                  className="user-profile-avatar-edit"
                  aria-label={siteTexts.profile.user.editAvatarPhoto}
                  title={siteTexts.profile.user.editAvatarPhoto}
                  onClick={openFilePicker}
                >
                  <span
                    className="material-symbols-outlined"
                    aria-hidden="true"
                  >
                    edit
                  </span>
                </button>
              )}
            />
          </div>
          <div>
            <p className="user-profile-name">{user.name}</p>
            <p className="user-profile-email">{user.email}</p>
            {!!user.pendingEmail && (
              <p className="user-profile-role">
                {siteTexts.profile.user.unconfirmedEmail}
              </p>
            )}
            <p className="user-profile-role">{profileRoleLabel(user.role)}</p>
          </div>
        </div>

        <div className="user-profile-athlete-stats">
          <div className="user-profile-stat user-profile-stat--matches">
            <span className="material-symbols-outlined" aria-hidden="true">
              group
            </span>
            <strong>{stats.matches}</strong>
            <small>{dashboardTexts.stats.matches}</small>
          </div>
          <div className="user-profile-stat user-profile-stat--titles">
            <span className="material-symbols-outlined" aria-hidden="true">
              emoji_events
            </span>
            <strong>{stats.titles}</strong>
            <small className="info-label">
              {dashboardTexts.stats.titles}
              <InfoTooltip content={info.titles} />
            </small>
          </div>
          <div className="user-profile-stat user-profile-stat--runner-up">
            <span className="material-symbols-outlined" aria-hidden="true">
              workspace_premium
            </span>
            <strong>{stats.runnersUp}</strong>
            <small className="info-label">
              {dashboardTexts.stats.runnersUp}
              <InfoTooltip content={info.runnerUps} />
            </small>
          </div>
          <div className="user-profile-stat user-profile-stat--category">
            <span className="material-symbols-outlined" aria-hidden="true">
              leaderboard
            </span>
            <strong>{stats.category}</strong>
            <small className="info-label">
              {dashboardTexts.stats.category}
              <InfoTooltip content={info.currentCategory} />
            </small>
          </div>
          <div className="user-profile-stat user-profile-stat--ranking">
            <span className="material-symbols-outlined" aria-hidden="true">
              trending_up
            </span>
            <strong>{stats.rankingPosition ? `#${stats.rankingPosition}` : "-"}</strong>
            <small className="info-label">
              {dashboardTexts.stats.rankingPosition}
              <InfoTooltip content={info.rankingPosition} />
            </small>
          </div>
        </div>

        <GradientShareButton
          className="user-profile-share-button"
          onClick={onOpenShare}
        >
          {dashboardTexts.shareProfile}
        </GradientShareButton>

        <div className="user-profile-nav">
          <Button
            type="button"
            variant={
              tab === "profile" &&
              !showCategoryRequestForm &&
              !hasActiveProfileAccordion
                ? "primary"
                : "pill"
            }
            onClick={onSelectOverview}
          >
            <span>
              <span className="material-symbols-outlined" aria-hidden="true">
                person
              </span>
              {siteTexts.profile.user.tabs.profile}
            </span>
            <span>›</span>
          </Button>

          {navItems.map((item) => {
            if (item.organizerOnly && user.role !== "organizer") return null;
            return (
              <Button
                key={item.key}
                type="button"
                variant={openProfileAccordions[item.key] ? "primary" : "pill"}
                onClick={() => onNavigateAccordion(item.key)}
              >
                <span>
                  <span
                    className="material-symbols-outlined"
                    aria-hidden="true"
                  >
                    {item.icon}
                  </span>
                  {item.label}
                </span>
                <span>›</span>
              </Button>
            );
          })}

          <Button
            type="button"
            variant={
              tab === "profile" && showCategoryRequestForm ? "primary" : "pill"
            }
            className="user-profile-category-request-nav"
            onClick={onOpenCategoryRequest}
          >
            <span>
              <span className="material-symbols-outlined" aria-hidden="true">
                swap_horiz
              </span>
              {siteTexts.profile.user.requestCategoryChange}
            </span>
            <span>›</span>
          </Button>
        </div>
      </div>
    </aside>
  );
}
